import { useTranslations } from "next-intl";
import { memo } from "react";
import { useProductsRented } from "@hooks/use-current-user";
import { useFiltersList } from "@hooks/use-features";
import shouldHideFilterSectionInListingPage from "@utils/catalog-filters/shouldHideFilterSectionInListingPage";
import SectionButton from "./Elements/SectionButton";
import SectionContent from "./Elements/SectionContent";
import FilterRadio from "./Elements/FilterRadio";
import catalogContexts from "@utils/constants/catalogContexts";

const filterSectionKey = "personalised";

const PersonalisedSection = memo(function PersonalisedSection({
  listingPage,
  onClose,
  filterSectionOpened,
  setFilterSectionOpened,
  checkedPersonalised,
  updatePersonalisedFilters,
  context,
  productsLiked,
  userListProducts,
}) {
  const t = useTranslations("catalog");

  const filtersList = useFiltersList();
  const productsRented = useProductsRented();

  if (!productsRented?.length && checkedPersonalised === "all") return null;
  if (shouldHideFilterSectionInListingPage(listingPage, "personalised")) return null;

  const sectionTitle = t(`filters.section_titles.${filterSectionKey}`);

  function removePersonalisedFilters() {
    let newFiltersConfigure = "";

    if (context === catalogContexts.wishlist) {
      newFiltersConfigure = productsLiked.map((id) => `id:${id}`).join(" OR ");
    }

    if (context === catalogContexts.userlist) {
      newFiltersConfigure = userListProducts.map((id) => `id:${id}`).join(" OR ");
    }

    updatePersonalisedFilters("all", newFiltersConfigure);
  }

  function filterOnProducts(filter) {
    let newFiltersConfigure = "";
    let personalisedQuery = "";

    if (filter === "rented") {
      personalisedQuery = productsRented.map((id) => `id:${id}`).join(" OR ");
    } else if (filter === "not_rented") {
      personalisedQuery = `NOT ${productsRented.map((id) => `id:${id}`).join(" AND NOT ")}`;
    }

    if ([catalogContexts.wishlist, catalogContexts.userlist].includes(context)) {
      const products = context === catalogContexts.wishlist ? productsLiked : userListProducts;
      const likedQuery = products.map((id) => `id:${id}`).join(" OR ");

      newFiltersConfigure = `${likedQuery} AND (${personalisedQuery})`;
    } else {
      newFiltersConfigure = personalisedQuery;
    }

    updatePersonalisedFilters(filter, newFiltersConfigure);
  }

  function displayPersonalisedFiltersOptions() {
    return filtersList.personalised.map((filter) => {
      const item = { isRefined: checkedPersonalised === filter, label: filter };

      return (
        <FilterRadio
          key={`filter-personalised-${filter}`}
          id={`filter-personalised-${filter}`}
          label={t(`filters.personalised.${filter}`)}
          filterType="personalised"
          filterSectionKey={filterSectionKey}
          item={item}
          onClick={() => filterOnProducts(filter)}
        />
      );
    });
  }

  const checkedFilters = {
    personalised: checkedPersonalised !== "all" ? [checkedPersonalised] : [],
  };

  return (
    <div>
      <SectionButton
        checkedFilters={checkedFilters}
        filterTypesToDisplay={["personalised"]}
        title={sectionTitle}
        onClick={() => setFilterSectionOpened(filterSectionKey)}
      />

      <SectionContent
        isOpen={filterSectionOpened === filterSectionKey}
        onClose={onClose}
        onGoBack={() => setFilterSectionOpened("")}
        title={sectionTitle}
      >
        <FilterRadio
          key="filter-personalised-all"
          id="filter-personalised-all"
          label={t("filters.personalised.all")}
          filterType="personalised"
          filterSectionKey={filterSectionKey}
          item={{ isRefined: checkedPersonalised === "all", label: "all", value: [] }}
          onClick={removePersonalisedFilters}
        />

        {displayPersonalisedFiltersOptions()}
      </SectionContent>
    </div>
  );
});

export default PersonalisedSection;
